import React, { useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Bot, MapPin } from "lucide-react";
import { toast } from "sonner";
import DealCard from "@/components/DealCard";

export default function Travel() {
  const [form, setForm] = useState({
    destination: "Hawaii",
    start_date: "",
    end_date: "",
    travelers: 2,
    budget: 3000,
    hotel_pref: "Family-friendly",
    airline_pref: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const optimize = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/travel-optimizer", form);
      setResult(data);
    } catch (e) {
      toast.error("Couldn't optimize");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-travel">
      <PageHeader eyebrow="Travel Optimizer" title="AI plans your next trip" subtitle="Bundle flights, hotels, and travel cards for max cashback." />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="mb-1.5 block flex items-center gap-1"><MapPin className="w-3 h-3" /> Destination</Label>
              <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="trav-destination" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Start</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block">End</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Travelers</Label>
                <Input type="number" min="1" value={form.travelers} onChange={(e) => setForm({ ...form, travelers: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label className="mb-1.5 block">Budget ($)</Label>
                <Input type="number" min="100" step="100" value={form.budget} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) })} data-testid="trav-budget" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Hotel preference</Label>
              <Input value={form.hotel_pref} onChange={(e) => setForm({ ...form, hotel_pref: e.target.value })} placeholder="Family-friendly, beachfront…" />
            </div>
            <div>
              <Label className="mb-1.5 block">Airline preference</Label>
              <Input value={form.airline_pref} onChange={(e) => setForm({ ...form, airline_pref: e.target.value })} placeholder="Optional" />
            </div>
            <Button onClick={optimize} disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="trav-optimize">
              <Plane className="w-4 h-4 mr-2" /> {loading ? "Optimizing…" : "AI optimize trip"}
            </Button>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {result ? (
            <>
              <Card className="border-sky-100 shadow-none rounded-2xl gradient-sky">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-4 h-4 text-sky-700" />
                    <div className="text-xs uppercase tracking-[0.18em] text-sky-700 font-semibold">AI travel plan</div>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap" data-testid="trav-plan">{result.plan}</p>
                  <div className="mt-4 inline-flex items-center gap-2 bg-white border border-emerald-200 px-4 py-2 rounded-full">
                    <span className="text-xs text-gray-600">Estimated cashback:</span>
                    <span className="font-display font-bold text-emerald-700">{fmtMoney(result.estimated_cashback)}</span>
                  </div>
                </CardContent>
              </Card>

              <div>
                <h3 className="font-display font-bold text-lg mb-3">Recommended travel offers</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {(result.recommended_offers || []).slice(0, 4).map((o) => (
                    <DealCard key={o.id} offer={o} onShop={() => toast.info("Click → Book on merchant site to earn cashback.")} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <Card className="border-gray-200 shadow-none rounded-2xl">
              <CardContent className="p-12 text-center">
                <Plane className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <h3 className="font-display font-bold text-lg">Where do you want to go?</h3>
                <p className="text-gray-600 text-sm mt-1">Tell us about your trip and Ria will plan the highest-cashback path.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
